import { FlashList } from '@shopify/flash-list';
import {
  addDays,
  addHours,
  addMinutes,
  format,
  getHours,
  getMinutes,
  setHours,
  setMinutes,
  startOfHour,
} from 'date-fns';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import {
  useFocusEffect,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { RentalLocation } from '@/api';
import type { Car } from '@/api/cars/types';
import type { Filter } from '@/api/cars/use-car-filters';
import { FilterName, useCarFilters } from '@/api/cars/use-car-filters';
import { useCarsAvailable } from '@/api/cars/use-cars-available';
import { useCarsAvailableCount } from '@/api/cars/use-cars-available-count';
import { useCustomer } from '@/api/customers/use-customer';
import { useDiscount } from '@/api/discounts/use-discount';
import { useOrganizationLocations } from '@/api/organizations/use-organization-locations';
import type { Address } from '@/api/subscriptions/types';
import Home from '@/components/home';
import { MapView } from '@/components/map-view';
import NavbarDropDown from '@/components/navbar/dropdown';
import OrganizationLocationInput from '@/components/organization-location-input';
import type { CarMarkerProps } from '@/components/search/car-marker';
import DeliveryDropdown from '@/components/search/delivery-dropdown';
import { MapCarCarousel } from '@/components/search/map-car-carousel';
import { SearchBarButton } from '@/components/search-bar-button';
import { isDesktop, midpoint, titleize, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import {
  getOrganizationSlug,
  useCurrentOrganization,
} from '@/core/organization';
import { useAttributionStore } from '@/stores/use-attribution-store';
import useSearchStore from '@/stores/use-search-store';
import { Button, Text } from '@/ui';
import ButtonFilter from '@/ui/button-filter';
import { Calendar } from '@/ui/calendar';
import CarCard from '@/ui/car-card';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { Search as SearchIcon } from '@/ui/icons';
import { Person } from '@/ui/icons';
import { ArrowClockwise } from '@/ui/icons/arrow-clockwise';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { FlagEmpty } from '@/ui/icons/flag-empty';
import { FlagFilled } from '@/ui/icons/flag-filled';
import { List } from '@/ui/icons/list';
import { Map as MapIcon } from '@/ui/icons/map';
import { Loader } from '@/ui/loader';
import MapCarCard from '@/ui/map-car-card';

import { EonOfferModal } from './eon-offer-modal';

const PAGE_HEIGHT = Dimensions.get('window').height;

const subscriptionDark = require('../../../../public/images/homepage/subscription-dark.png');
const subscriptionLight = require('../../../../public/images/homepage/subscription-light.png');

const subscriptionCardDark = require('../../../../public/images/homepage/subscription-card-dark.png');
const subscriptionCardLight = require('../../../../public/images/homepage/subscription-card-light.png');

const longArrow = require('../../../../public/images/long-arrow.png');

const parseFilterValues = (filters: Filter[]) => {
  const modelIdx = filters.findIndex((f) => f.name === FilterName.MODEL);
  const modelIds = modelIdx > -1 ? filters[modelIdx].selected : [];
  const selectedModels = filters[modelIdx]?.options
    .filter((f, idx) => modelIds.includes(idx))
    .map((f) => f.name);

  const autopilotIdx = filters.findIndex(
    (f) => f.name === FilterName.AUTOPILOT
  );
  const autopilotTypes =
    autopilotIdx > -1 ? filters[autopilotIdx].selected : [];
  const selectedAutopilotTypes = filters[autopilotIdx]?.options
    .filter((f, idx) => autopilotTypes.includes(idx))
    .map((f) => f.value);

  const numSeatsIdx = filters.findIndex((f) => f.name === FilterName.SEATS);
  const numSeats = numSeatsIdx > -1 ? filters[numSeatsIdx].selected : [];
  const selectedNumSeats = filters[numSeatsIdx]?.options
    .filter((f, idx) => numSeats.includes(idx))
    .map((f) => f.value);

  const batteryRangeIdx = filters.findIndex((f) => f.name === FilterName.RANGE);
  const batteryRanges =
    batteryRangeIdx > -1 ? filters[batteryRangeIdx].selected : [];
  const selectedBatteryRanges = filters[batteryRangeIdx]?.options
    .filter((f, idx) => batteryRanges.includes(idx))
    .map((f) => f.value);

  const priceIdx = filters.findIndex((f) => f.name === FilterName.PRICE);
  const priceRange = filters[priceIdx]?.selected;

  const sortIdx = filters.findIndex((f) => f.name === FilterName.SORT_BY);
  const sort = filters[sortIdx]?.options[filters[sortIdx]?.selected].value;

  return {
    models: selectedModels,
    autopilotTypes: selectedAutopilotTypes,
    numSeats: selectedNumSeats,
    batteryRanges: selectedBatteryRanges,
    minDailyPrice: priceRange && priceRange[0],
    maxDailyPrice: priceRange && priceRange[1],
    sort,
  };
};

const subdomain = getOrganizationSlug();

export default function Search() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  // This screen is a tab, so it stays mounted (and mounts lazily on first
  // visit) while another tab owns the URL. The effects below write the address
  // bar directly, so they must stay inert unless the URL is actually ours --
  // otherwise they stamp search params onto whatever route is showing.
  const ownsUrl = usePathname() === '/search';
  const status = useAuth.use.status();
  const address = useSearchStore((state) => state.address);
  const deliveryDeliverToAddress = useSearchStore(
    (state) => state.deliveryDeliverToAddress
  );
  const deliveryReturnAddress = useSearchStore(
    (state) => state.deliveryReturnAddress
  );
  const deliveryModeEnabled = useSearchStore(
    (state) => state.deliveryModeEnabled
  );
  const startDatetime = useSearchStore((state) => state.startDatetime);
  const endDatetime = useSearchStore((state) => state.endDatetime);
  const setAddress = useSearchStore((state) => state.setAddress);
  const setStartDatetime = useSearchStore((state) => state.setStartDatetime);
  const setEndDatetime = useSearchStore((state) => state.setEndDatetime);
  const setDeliveryModeEnabled = useSearchStore(
    (state) => state.setDeliveryModeEnabled
  );
  const setDeliveryDeliverToAddress = useSearchStore(
    (state) => state.setDeliveryDeliverToAddress
  );
  const setDeliveryReturnToAddress = useSearchStore(
    (state) => state.setDeliveryReturnToAddress
  );
  const filters = useSearchStore((state) => state.filters);
  const setFilters = useSearchStore((state) => state.setFilters);
  const [tempFilters, setTempFilters] = React.useState<Filter[]>([]);
  const [activeFilterIdx, setActiveFilterIdx] = React.useState<number | null>();
  const viewMode = useSearchStore((state) => state.viewMode);
  const setViewMode = useSearchStore((state) => state.setViewMode);
  const locationId = useSearchStore((state) => state.locationId);
  const setLocationId = useSearchStore((state) => state.setLocationId);
  const code = useSearchStore((state) => state.code);
  const promo = useSearchStore((state) => state.promo);
  const hidePromoPopup = useSearchStore((state) => state.hidePromoPopup);
  const setCode = useSearchStore((state) => state.setCode);
  const setPromo = useSearchStore((state) => state.setPromo);
  const setHidePromoPopup = useSearchStore((state) => state.setHidePromoPopup);
  const { organization } = useCurrentOrganization();
  const [showDropdown, setShowDropdown] = React.useState(false);
  const mapRef = useRef(null);
  const [selectedLocation, setSelectedLocation] =
    React.useState<RentalLocation>();
  const [boundaries, setBoundaries] = React.useState<any>(null);
  const [enableMapRefresh, setEnableMapRefresh] = React.useState(false);
  const setCar = useSearchStore((state) => state.setCar);
  const [showGoogleAutoComplete, setShowGoogleAutoComplete] =
    React.useState(false);
  const [showStartTimeCalendar, setShowStartTimeCalendar] =
    React.useState(false);
  const [showEonOfferModal, setShowEonOfferModal] = React.useState(false);
  const [showEndTimeCalendar, setShowEndTimeCalendar] = React.useState(false);
  const [defaultCenterPosition, setDefaultCenterPosition] = useState({
    latitude: 40.758916,
    longitude: -73.967476,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [smartbannerHeight, setSmartbannerHeight] = useState(0);
  const searchContainerRef = useRef(null);
  const local = useLocalSearchParams<{
    start_datetime: string;
    end_datetime: string;
    address?: string;
    address_lat?: string;
    address_lng?: string;
    mode?: 'pickup' | 'delivery';
    delivery_address?: string;
    delivery_address_lat?: string;
    delivery_address_lng?: string;
    return_address?: string;
    return_address_lat?: string;
    return_address_lng?: string;
    autopilot_types?: string;
    battery_ranges?: string;
    max_daily_price?: string;
    min_daily_price?: string;
    models?: string;
    num_seats?: string;
    sort?: string;
    code?: string;
    promo?: string;
    view?: 'map' | 'list';
  }>();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Search',
    });
  }, []);

  const { pid, c } = useAttributionStore();

  const { data: filtersFromApi } = useCarFilters();

  const { data: customer, refetch: refetchCustomer } = useCustomer();

  const { data: discount, refetch: refetchDiscount } = useDiscount({
    enabled: false,
    variables: { code: local.code || code || '' },
  });

  useEffect(() => {
    if (status === 'signIn' && !customer) {
      refetchCustomer();
    }
  }, [customer, status, refetchCustomer]);

  const filterValues = parseFilterValues(filters);
  const tempFilterValues = parseFilterValues(tempFilters);

  const minDate = useMemo(() => {
    return startOfHour(addMinutes(addHours(new Date(), 3), 60));
  }, []);

  const defaultStartDate = useMemo(() => {
    if (startDatetime) {
      return startDatetime;
    }

    return startOfHour(addMinutes(addDays(new Date(), 3), 60));
  }, [startDatetime]);

  const defaultEndDate = useMemo(() => {
    if (endDatetime) {
      return endDatetime;
    }

    return addDays(minDate, 3);
  }, [endDatetime, minDate]);

  React.useEffect(() => {
    if (code) {
      refetchDiscount();
    }
  }, [code, refetchDiscount]);

  React.useEffect(() => {
    if (local.code) {
      setCode(local.code);
    }
  }, [local.code, setCode]);

  React.useEffect(() => {
    if (local.promo) {
      setPromo(local.promo);
    }
  }, [local.promo, setPromo]);

  React.useEffect(() => {
    if (local.code && code && local.code !== code) {
      setHidePromoPopup(false);
    }
  }, [local.code, code]);

  React.useEffect(() => {
    if (discount && !hidePromoPopup) {
      setShowEonOfferModal(true);
      setHidePromoPopup(true);
    }
  }, [discount, hidePromoPopup]);

  React.useEffect(() => {
    if (local.start_datetime) {
      setStartDatetime(new Date(local.start_datetime));
    }
  }, [local.start_datetime, setStartDatetime]);

  React.useEffect(() => {
    if (local.end_datetime) {
      setEndDatetime(new Date(local.end_datetime));
    }
  }, [local.end_datetime, setEndDatetime]);

  React.useEffect(() => {
    setDeliveryModeEnabled(local.mode === 'delivery');
  }, [local.mode, setDeliveryModeEnabled]);

  React.useEffect(() => {
    const view = Array.isArray(local.view) ? local.view[0] : local.view;
    if (view === 'list' || view === 'map') {
      setViewMode(view);
    }
  }, [local.view, setViewMode]);

  React.useEffect(() => {
    if (local.address && local.address_lat && local.address_lng) {
      setAddress({
        description: local.address || '',
        latitude: parseFloat(local.address_lat),
        longitude: parseFloat(local.address_lng),
      });
    }
  }, [local.address, local.address_lat, local.address_lng, setAddress]);

  // Default to the org's busiest location (backend sorts by car count desc) on
  // org subdomains. Applied once on arrival, and it won't override an address
  // the user already picked or one passed via the URL.
  const { data: organizationLocations } = useOrganizationLocations();
  const didApplyDefaultAddressRef = React.useRef(false);
  React.useEffect(() => {
    if (didApplyDefaultAddressRef.current) return;
    if (!organizationLocations?.length) return;
    didApplyDefaultAddressRef.current = true;

    if (address || local.address) return;

    const [busiest] = organizationLocations;
    setAddress({
      description: busiest.address || busiest.city,
      latitude: busiest.latitude,
      longitude: busiest.longitude,
    });
  }, [organizationLocations, address, local.address, setAddress]);

  React.useEffect(() => {
    const refetchBoundaries = async () => {
      if (address) {
        const bounds = await mapRef.current?.getBounds();

        if (!bounds) return null;

        setBoundaries({
          topRight: {
            lat: bounds.northEast.latitude,
            lng: bounds.northEast.longitude,
          },
          bottomLeft: {
            lat: bounds.southWest.latitude,
            lng: bounds.southWest.longitude,
          },
        });
      }
    };

    refetchBoundaries();
  }, [address]);

  React.useEffect(() => {
    if (
      local.delivery_address &&
      local.delivery_address_lat &&
      local.delivery_address_lng
    ) {
      setDeliveryDeliverToAddress({
        description: local.delivery_address || '',
        latitude: parseFloat(local.delivery_address_lat),
        longitude: parseFloat(local.delivery_address_lng),
      });
    }
  }, [
    local.delivery_address,
    local.delivery_address_lat,
    local.delivery_address_lng,
    setDeliveryDeliverToAddress,
    deliveryModeEnabled,
  ]);

  React.useEffect(() => {
    if (
      local.return_address &&
      local.return_address_lat &&
      local.return_address_lng
    ) {
      setDeliveryReturnToAddress({
        description: local.return_address || '',
        latitude: parseFloat(local.return_address_lat),
        longitude: parseFloat(local.return_address_lng),
      });
    }
  }, [
    local.return_address,
    local.return_address_lat,
    local.return_address_lng,
    setDeliveryReturnToAddress,
    deliveryModeEnabled,
  ]);

  React.useEffect(() => {
    if (ownsUrl && Platform.OS === 'web' && startDatetime) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set(
        'start_datetime',
        format(startDatetime, 'yyyy-MM-dd HH:mm:ss')
      );
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [startDatetime, ownsUrl]);

  React.useEffect(() => {
    if (ownsUrl && Platform.OS === 'web' && endDatetime) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set(
        'end_datetime',
        format(endDatetime, 'yyyy-MM-dd HH:mm:ss')
      );
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [endDatetime, ownsUrl]);

  React.useEffect(() => {
    if (ownsUrl && Platform.OS === 'web') {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('mode', deliveryModeEnabled ? 'delivery' : 'pickup');
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [deliveryModeEnabled, ownsUrl]);

  React.useEffect(() => {
    if (ownsUrl && Platform.OS === 'web' && address) {
      const url = new URL(window.location.href);

      url.searchParams.set('address', address.description);
      url.searchParams.set('address_lat', address.latitude.toString());
      url.searchParams.set('address_lng', address.longitude.toString());

      window.history.replaceState({}, '', url.toString());
    }
  }, [address, ownsUrl]);

  React.useEffect(() => {
    if (!ownsUrl || Platform.OS !== 'web') return;

    // if (!deliveryModeEnabled) {
    //   const url = new URL(window.location.href);

    //   url.searchParams.delete('delivery_address');
    //   url.searchParams.delete('delivery_address_lat');
    //   url.searchParams.delete('delivery_address_lng');

    //   url.searchParams.delete('return_address');
    //   url.searchParams.delete('return_address_lat');
    //   url.searchParams.delete('return_address_lng');

    //   window.history.replaceState({}, '', url.toString());

    //   setDeliveryDeliverToAddress(null);
    //   setDeliveryReturnToAddress(null);
    // }

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      const url = new URL(window.location.href);

      url.searchParams.set(
        'delivery_address',
        deliveryDeliverToAddress.description
      );
      url.searchParams.set(
        'delivery_address_lat',
        deliveryDeliverToAddress.latitude.toString()
      );
      url.searchParams.set(
        'delivery_address_lng',
        deliveryDeliverToAddress.longitude.toString()
      );

      url.searchParams.set('return_address', deliveryReturnAddress.description);
      url.searchParams.set(
        'return_address_lat',
        deliveryReturnAddress.latitude.toString()
      );
      url.searchParams.set(
        'return_address_lng',
        deliveryReturnAddress.longitude.toString()
      );

      window.history.replaceState({}, '', url.toString());
    }
  }, [
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    deliveryModeEnabled,
    ownsUrl,
  ]);

  React.useEffect(() => {
    if (ownsUrl && Platform.OS === 'web' && filterValues) {
      const queryParams = new URLSearchParams(window.location.search);

      if (filterValues.autopilotTypes?.length > 0) {
        queryParams.set(
          'autopilot_types',
          filterValues.autopilotTypes.join(',')
        );
      } else if (queryParams.get('autopilot_types')) {
        queryParams.delete('autopilot_types');
      }

      if (filterValues.batteryRanges?.length > 0) {
        queryParams.set('battery_ranges', filterValues.batteryRanges.join(','));
      } else if (queryParams.get('battery_ranges')) {
        queryParams.delete('battery_ranges');
      }

      if (filterValues.maxDailyPrice) {
        queryParams.set(
          'max_daily_price',
          filterValues.maxDailyPrice.toString()
        );
      } else if (queryParams.get('max_daily_price')) {
        queryParams.set('max_daily_price', '1000');
      }

      if (filterValues.minDailyPrice) {
        queryParams.set(
          'min_daily_price',
          filterValues.minDailyPrice.toString()
        );
      } else if (queryParams.get('min_daily_price')) {
        queryParams.set('min_daily_price', '10');
      }

      if (filterValues.models?.length > 0) {
        queryParams.set('models', filterValues.models.join(','));
      } else if (queryParams.get('models')) {
        queryParams.delete('models');
      }

      if (filterValues.numSeats?.length > 0) {
        queryParams.set('num_seats', filterValues.numSeats.join(','));
      } else if (queryParams.get('num_seats')) {
        queryParams.delete('num_seats');
      }

      if (filterValues.sort) {
        queryParams.set('sort', filterValues.sort);
      } else if (queryParams.get('sort')) {
        queryParams.set('sort', 'address');
      }

      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [filterValues, ownsUrl]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowGoogleAutoComplete(false);
        setShowStartTimeCalendar(false);
        setShowEndTimeCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchContainerRef]);

  useEffect(() => {
    setTimeout(() => {
      // router.navigate('/trips');
      // router.navigate('/subscriptions/thank-you');
      // router.navigate('/checkout-complete/4402');
    }, 100);
  }, [router]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleClickOutside = (event) => {
        if (
          showStartTimeCalendar &&
          !document
            .getElementById('start-time-calendar')
            ?.contains(event.target)
        ) {
          setShowStartTimeCalendar(false);
        }

        if (
          showEndTimeCalendar &&
          !document.getElementById('end-time-calendar')?.contains(event.target)
        ) {
          setShowEndTimeCalendar(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showStartTimeCalendar, showEndTimeCalendar]);

  const paginate = !isDesktop() && viewMode === 'list';

  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetched,
    refetch: refetchCars,
  } = useCarsAvailable({
    paginate: paginate,
    address: address?.description || '',
    latitude: address?.latitude,
    longitude: address?.longitude,
    boundingBox: boundaries || '',
    startDatetime: format(startDatetime, 'yyyy-MM-dd HH:mm:ss') || '',
    endDatetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss') || '',
    deliveryAddress:
      (deliveryModeEnabled && deliveryDeliverToAddress?.description) || '',
    returnAddress:
      (deliveryModeEnabled && deliveryReturnAddress?.description) || '',
    ...filterValues,
  });

  const { data: carsCount, refetch: refetchCount } = useCarsAvailableCount({
    variables: {
      address: address?.description || '',
      latitude: address?.latitude,
      longitude: address?.longitude,
      boundingBox: boundaries || '',
      startDatetime: format(startDatetime, 'yyyy-MM-dd HH:mm:ss') || '',
      endDatetime: format(endDatetime, 'yyyy-MM-dd HH:mm:ss') || '',
      deliveryAddress:
        (deliveryModeEnabled && deliveryDeliverToAddress?.description) || '',
      returnAddress:
        (deliveryModeEnabled && deliveryReturnAddress?.description) || '',
      ...tempFilterValues,
    },
    enabled:
      (!!address || (!!deliveryDeliverToAddress && !!deliveryReturnAddress)) &&
      !!startDatetime &&
      !!endDatetime &&
      startDatetime < endDatetime,
  });

  // useEffect(() => {
  //   if (boundaries) {
  //     refetchCount();
  //     refetchCars();
  //   }
  // }, [boundaries, viewMode, refetchCount, refetchCars]);

  useEffect(() => {
    if (
      (address ||
        (deliveryDeliverToAddress && deliveryReturnAddress) ||
        boundaries) &&
      startDatetime &&
      endDatetime
    ) {
      refetchCars();
      refetchCount();
    }
  }, [
    address,
    boundaries,
    filters,
    locationId,
    startDatetime,
    endDatetime,
    deliveryModeEnabled,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    refetchCars,
    refetchCount,
  ]);

  // useEffect(() => {
  //   refetchCount();
  // }, [tempFilters, refetchCount]);

  useEffect(() => {
    if (filtersFromApi) {
      let updatedFilters = filtersFromApi;

      if (local.autopilot_types) {
        const autopilotTypes = local.autopilot_types.split(',').map(Number);

        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.AUTOPILOT) {
            const opts = f.options.reduce((acc, o, idx) => {
              if (autopilotTypes.includes(o.value)) {
                acc.push(idx);
              }

              return acc;
            }, []);

            return {
              ...f,
              selected: opts,
            };
          }

          return f;
        });
      }

      if (local.battery_ranges) {
        const batteryRanges = local.battery_ranges.split(',');

        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.RANGE) {
            const opts = f.options.reduce((acc, o, idx) => {
              if (batteryRanges.includes(o.value)) {
                acc.push(idx);
              }

              return acc;
            }, []);

            return {
              ...f,
              selected: opts,
            };
          }

          return f;
        });
      }

      if (local.max_daily_price && local.min_daily_price) {
        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.PRICE) {
            return {
              ...f,
              selected: [
                parseInt(local.min_daily_price),
                parseInt(local.max_daily_price),
              ],
            };
          }

          return f;
        });
      }

      if (local.models) {
        const modelNames = local.models.split(',');

        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.MODEL) {
            const opts = f.options.reduce((acc, o, idx) => {
              if (modelNames.includes(o.name)) {
                acc.push(idx);
              }

              return acc;
            }, []);

            return {
              ...f,
              selected: opts,
            };
          }

          return f;
        });
      }

      if (local.num_seats) {
        const numSeats = local.num_seats.split(',').map(Number);

        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.SEATS) {
            const opts = f.options.reduce((acc, o, idx) => {
              if (numSeats.includes(o.value)) {
                acc.push(idx);
              }

              return acc;
            }, []);

            return {
              ...f,
              selected: opts,
            };
          }

          return f;
        });
      }

      if (local.sort) {
        updatedFilters = updatedFilters.map((f) => {
          if (f.name === FilterName.SORT_BY) {
            const oIdx = f.options.findIndex((o) => o.value === local.sort);

            return {
              ...f,
              selected: oIdx,
            };
          }

          return f;
        });
      }

      setFilters(updatedFilters);
    }
  }, [
    local.autopilot_types,
    local.battery_ranges,
    local.max_daily_price,
    local.min_daily_price,
    local.models,
    local.num_seats,
    local.sort,
    filtersFromApi,
    setFilters,
  ]);

  const allCars = useMemo(() => {
    return data?.pages.map((page) => page.data).flat();
  }, [data]);

  const cars = useMemo(() => {
    if (locationId) {
      return data?.pages
        .map((page) => page.data)
        .flat()
        .filter((car) => car.defaultLocation.id === locationId);
    }

    return data?.pages.map((page) => page.data).flat();
  }, [data, locationId]);

  const locationCars = useMemo(() => {
    if (!selectedLocation || !cars) {
      return [];
    }

    return cars.filter((car) => car.defaultLocation.id === selectedLocation.id);
  }, [cars, selectedLocation]);

  const totalCarsCount = useMemo(() => {
    if (paginate) {
      return data?.pages[0].meta.totalCount;
    }

    return data?.pages[0].data.length;
  }, [data, paginate]);

  const searchTimesError = useMemo(() => {
    if (startDatetime === endDatetime) {
      return 'Start and end times cannot be the same.';
    }

    if (endDatetime <= startDatetime) {
      return 'End time must be after the start time.';
    }

    if (startDatetime >= endDatetime) {
      return 'Start time must be before the end time.';
    }

    return '';
  }, [startDatetime, endDatetime]);

  // const polylines = useMemo(() => {
  //   return selectedLocation &&
  //     deliveryModeEnabled &&
  //     deliveryDeliverToAddress &&
  //     deliveryReturnAddress
  //     ? [
  //         {
  //           coordinates: [
  //             {
  //               latitude: selectedLocation.latitude,
  //               longitude: selectedLocation.longitude,
  //             },
  //             {
  //               latitude: deliveryDeliverToAddress.latitude,
  //               longitude: deliveryDeliverToAddress.longitude,
  //             },
  //           ],
  //           strokeColor: PINKLING,
  //           strokeWidth: 2,
  //         },
  //         {
  //           coordinates: [
  //             {
  //               latitude: selectedLocation.latitude,
  //               longitude: selectedLocation.longitude,
  //             },
  //             {
  //               latitude: deliveryReturnAddress.latitude,
  //               longitude: deliveryReturnAddress.longitude,
  //             },
  //           ],
  //           strokeColor: PINKLING,
  //           strokeWidth: 2,
  //         },
  //       ]
  //     : [];
  // }, [
  //   deliveryDeliverToAddress,
  //   deliveryReturnAddress,
  //   selectedLocation,
  //   deliveryModeEnabled,
  // ]);

  const groupCarsByLocation = (cars: Car[]): Map<string, Car[]> => {
    const groupedCars = new Map<string, Car[]>();

    cars.forEach((car) => {
      const locationKey = `${car.defaultLocation.latitude},${car.defaultLocation.longitude}`;

      if (!groupedCars.has(locationKey)) {
        groupedCars.set(locationKey, []);
      }
      groupedCars.get(locationKey)?.push(car);
    });

    return groupedCars;
  };

  const markers = useMemo(() => {
    let ms: CarMarkerProps[] = [];

    if (address && !deliveryModeEnabled) {
      ms = [
        ...ms,
        {
          latitude: address.latitude,
          longitude: address.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagFilled />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
        },
      ];
    }

    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      ms = [
        ...ms,
        {
          latitude: deliveryDeliverToAddress.latitude,
          longitude: deliveryDeliverToAddress.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagEmpty />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
          active: !!selectedLocation,
        },
        {
          latitude: deliveryReturnAddress.latitude,
          longitude: deliveryReturnAddress.longitude,
          icon: (
            <Icon
              type="naked"
              icon={<FlagFilled />}
              fillColor={theme.colors.icon.color}
              width={20}
              height={20}
            />
          ),
          active: !!selectedLocation,
        },
      ];
    }

    // if (
    //   selectedLocation &&
    //   deliveryModeEnabled &&
    //   deliveryDeliverToAddress &&
    //   deliveryReturnAddress
    // ) {
    //   ms = [
    //     ...ms,
    //     {
    //       latitude: midpoint(
    //         selectedLocation.latitude,
    //         selectedLocation.longitude,
    //         deliveryDeliverToAddress.latitude,
    //         deliveryDeliverToAddress.longitude
    //       ).latitude,
    //       longitude: midpoint(
    //         selectedLocation.latitude,
    //         selectedLocation.longitude,
    //         deliveryDeliverToAddress.latitude,
    //         deliveryDeliverToAddress.longitude
    //       ).longitude,
    //       text: `${formatNumber(
    //         cars?.filter((c) => c.defaultLocation.id === selectedLocation.id)[0]
    //           .deliveryDistance
    //       )} mi`,
    //       active: true,
    //     },
    //     {
    //       latitude: midpoint(
    //         selectedLocation.latitude,
    //         selectedLocation.longitude,
    //         deliveryReturnAddress.latitude,
    //         deliveryReturnAddress.longitude
    //       ).latitude,
    //       longitude: midpoint(
    //         selectedLocation.latitude,
    //         selectedLocation.longitude,
    //         deliveryReturnAddress.latitude,
    //         deliveryReturnAddress.longitude
    //       ).longitude,
    //       text: `${formatNumber(
    //         cars?.filter((c) => c.defaultLocation.id === selectedLocation.id)[0]
    //           .returnDistance
    //       )} mi`,
    //       active: true,
    //     },
    //   ];
    // }

    if (cars) {
      Array.from(groupCarsByLocation(allCars)).forEach(
        ([locationKey, groupedCars]) => {
          const [latitude, longitude] = locationKey.split(',').map(Number);

          let text;
          if (groupedCars.length > 1) {
            const minRate = Math.min(
              ...groupedCars.map((car) => car.dailyRate)
            );

            text = `${groupedCars.length} Cars From $${minRate}`;
          } else {
            text = `$${groupedCars[0].dailyRate}`;
          }

          ms = [
            ...ms,
            {
              latitude,
              longitude,
              text,
              onPress: (e: any) => {
                if (isDesktop() && groupedCars.length > 1) {
                  setLocationId(groupedCars[0].defaultLocation.id);
                } else {
                  setLocationId(undefined);
                }

                setDefaultCenterPosition(e);

                setSelectedLocation(groupedCars[0].defaultLocation);
              },
              infoWindow: isDesktop() && groupedCars.length === 1 && (
                <MapCarCard
                  car={groupedCars[0]}
                  onPress={() => {
                    setCar(groupedCars[0]);

                    if (Platform.OS === 'web') {
                      const queryParams = new URLSearchParams();

                      if (startDatetime) {
                        queryParams.append(
                          'start_datetime',
                          format(startDatetime, 'yyyy-MM-dd HH:mm:ss')
                        );
                      }

                      if (endDatetime) {
                        queryParams.append(
                          'end_datetime',
                          format(endDatetime, 'yyyy-MM-dd HH:mm:ss')
                        );
                      }

                      if (deliveryModeEnabled) {
                        queryParams.append('mode', 'delivery');
                      } else {
                        queryParams.append('mode', 'pickup');
                      }

                      if (deliveryDeliverToAddress?.description) {
                        queryParams.append(
                          'delivery_address',
                          deliveryDeliverToAddress.description
                        );
                      }

                      if (deliveryDeliverToAddress?.latitude) {
                        queryParams.append(
                          'delivery_address_lat',
                          deliveryDeliverToAddress.latitude.toString()
                        );
                      }

                      if (deliveryDeliverToAddress?.longitude) {
                        queryParams.append(
                          'delivery_address_lng',
                          deliveryDeliverToAddress.longitude.toString()
                        );
                      }

                      if (deliveryReturnAddress?.description) {
                        queryParams.append(
                          'return_address',
                          deliveryReturnAddress.description
                        );
                      }

                      if (deliveryReturnAddress?.latitude) {
                        queryParams.append(
                          'return_address_lat',
                          deliveryReturnAddress.latitude.toString()
                        );
                      }

                      if (deliveryReturnAddress?.longitude) {
                        queryParams.append(
                          'return_address_lng',
                          deliveryReturnAddress.longitude.toString()
                        );
                      }

                      if (local.code) {
                        queryParams.append('code', local.code);
                      }

                      if (pid) {
                        queryParams.append('pid', pid);
                      }

                      if (c) {
                        queryParams.append('c', c);
                      }

                      if (local.promo) {
                        queryParams.append('promo', local.promo);
                      }

                      const url = `/car/${groupedCars[0].id}?${queryParams}`;

                      window.open(url, '_blank');
                    } else {
                      router.push({
                        pathname: '/car/[id]',
                        params: {
                          id: groupedCars[0].id,
                          start_datetime: format(
                            startDatetime,
                            'yyyy-MM-dd HH:mm:ss'
                          ),
                          end_datetime: format(
                            endDatetime,
                            'yyyy-MM-dd HH:mm:ss'
                          ),
                          delivery_address:
                            deliveryDeliverToAddress?.description,
                          delivery_address_lat:
                            deliveryDeliverToAddress?.latitude,
                          delivery_address_lng:
                            deliveryDeliverToAddress?.longitude,
                          return_address: deliveryReturnAddress?.description,
                          return_address_lat: deliveryReturnAddress?.latitude,
                          return_address_lng: deliveryReturnAddress?.longitude,
                          mode: deliveryModeEnabled ? 'delivery' : 'pickup',
                          code: code || undefined,
                          pid: pid || undefined,
                          c: c || undefined,
                          promo: promo || undefined,
                        },
                      });
                    }
                  }}
                />
              ),
              active:
                selectedLocation &&
                selectedLocation?.id === groupedCars[0].defaultLocation.id,
            },
          ];
        }
      );
    }

    return ms;
  }, [
    address,
    cars,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    selectedLocation,
    deliveryModeEnabled,
  ]);

  const defaultCenter = useMemo(() => {
    if (
      deliveryModeEnabled &&
      deliveryDeliverToAddress &&
      deliveryReturnAddress
    ) {
      const midpt = midpoint(
        deliveryDeliverToAddress.latitude,
        deliveryDeliverToAddress.longitude,
        deliveryReturnAddress.latitude,
        deliveryReturnAddress.longitude
      );

      return midpt;
    }

    if (address) {
      return {
        latitude: address.latitude,
        longitude: address.longitude,
      };
    }

    return { latitude: 40.758916, longitude: -73.967476 };
  }, [
    address,
    deliveryDeliverToAddress,
    deliveryReturnAddress,
    deliveryModeEnabled,
  ]);

  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS !== 'web') return;

      const checkSmartbanner = () => {
        const banner = document.querySelector('#smartbanner');
        const hasChildren = banner?.children.length > 0;
        const height = hasChildren ? banner.clientHeight : 0;
        setSmartbannerHeight(height);
      };

      checkSmartbanner();

      const observer = new MutationObserver(checkSmartbanner);
      const smartbanner = document.querySelector('#smartbanner');
      if (smartbanner) {
        observer.observe(smartbanner, { childList: true, subtree: true });
      }

      return () => observer.disconnect();
    }, [])
  );

  const modeButtonBottomOffset = useMemo(() => {
    if (Platform.OS === 'web') {
      if (smartbannerHeight > 0) {
        return smartbannerHeight + 85;
      }

      return 85;
    }

    return 24;
  }, [smartbannerHeight]);

  const renderSearchAndFilters = () => {
    return (
      <>
        {!boundaries && !address && !deliveryModeEnabled ? (
          <SearchBarButton
            onPress={() => {
              router.push({
                pathname: '/search-details',
                params: {
                  code: code || undefined,
                  promo: promo || undefined,
                },
              });
            }}
            title={'Search for an address'}
            style={{
              backgroundColor: theme.colors.card.hover.backgroundColor,
            }}
            icon={
              <Icon
                type="dressed"
                icon={<SearchIcon />}
                width={28}
                height={28}
                fillColor={theme.colors.chip.brand.color}
              />
            }
          />
        ) : (
          <SearchBarButton
            onPress={() => {
              router.push({
                pathname: '/search-details',
                params: {
                  code: local.code || undefined,
                  promo: local.promo || undefined,
                },
              });
            }}
            title={
              (deliveryModeEnabled && deliveryDeliverToAddress?.description) ||
              address?.description ||
              'Where'
            }
            caption={
              (startDatetime &&
                endDatetime &&
                `${format(startDatetime, 'MMM d | h:mm a')} - ${format(
                  endDatetime,
                  'MMM d | h:mm a'
                )}`) ||
              'From - To'
            }
          />
        )}

        {cars?.length > 0 ? (
          <ScrollView
            style={{ flexGrow: 0 }}
            horizontal
            showsHorizontalScrollIndicator={false}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 8,
              }}
            >
              {filters && filters.length > 0
                ? filters
                    .filter((f) => f.name !== FilterName.SORT_BY)
                    .map((filter: Filter, idx) => {
                      const formattedName = titleize(filter.name);

                      if (filter.collapsed) {
                        return null;
                      }

                      return (
                        <ButtonFilter
                          key={filter.name}
                          name={
                            filter.selected.length > 0
                              ? `${formattedName} ${
                                  filter.name !== FilterName.PRICE
                                    ? `(${filter.selected.length})`
                                    : ''
                                }`
                              : formattedName
                          }
                          headerTitle={formattedName}
                          handlePress={(selectedOptions: number[]) => {
                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected: selectedOptions,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setTempFilters(updatedFilters);
                          }}
                          options={filter.options}
                          onOpen={() => {
                            setActiveFilterIdx(idx);
                          }}
                          onClose={() => {
                            setTempFilters([]);
                            setActiveFilterIdx(null);
                          }}
                          onClearAll={() => {
                            let selected: any[] = [];

                            if (filter.name === FilterName.PRICE) {
                              selected = [10, 1000];
                            }

                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setFilters(updatedFilters);

                            const updatedTempFilters = [
                              ...tempFilters.slice(0, idx),
                              {
                                ...filter,
                                selected,
                              },
                              ...tempFilters.slice(idx + 1),
                            ];

                            setTempFilters(updatedTempFilters);
                          }}
                          onSave={(selectedOptions: number[]) => {
                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected: selectedOptions,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setFilters(updatedFilters);
                            setActiveFilterIdx(null);
                          }}
                          opened={activeFilterIdx === idx}
                          active={
                            activeFilterIdx === idx ||
                            (filter.name !== FilterName.PRICE &&
                              filter.selected.length > 0) ||
                            (filter.name === FilterName.PRICE &&
                              (filter.selected[0] !== 10 ||
                                filter.selected[1] !== 1000))
                          }
                          selected={filter.selected}
                          numResults={carsCount || 0}
                        />
                      );
                    })
                : null}
            </View>
          </ScrollView>
        ) : null}

        {viewMode === 'map' && enableMapRefresh ? (
          <Button
            tx="search.refresh"
            type="secondaryExtraSmall"
            leftIcon={
              <Icon
                type="naked"
                icon={<ArrowClockwise />}
                width={12}
                height={12}
                fillColor={theme.colors.button.secondary.default.text}
              />
            }
            onPress={async () => {
              const bounds = await mapRef.current?.getBounds();

              setBoundaries({
                topRight: {
                  lat: bounds.northEast.latitude,
                  lng: bounds.northEast.longitude,
                },
                bottomLeft: {
                  lat: bounds.southWest.latitude,
                  lng: bounds.southWest.longitude,
                },
              });

              setEnableMapRefresh(false);
              setLocationId(undefined);
            }}
            style={{
              width: 100,
              position: 'absolute',
              bottom: -48,
              left: '50%',
              transform: [{ translateX: -50 }],
            }}
          />
        ) : null}
      </>
    );
  };

  const renderNativeHome = () => {
    return <Home />;
  };

  if (isDesktop()) {
    return (
      <View
        style={{
          backgroundColor: theme.colors.background_1,
          flex: 1,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 100,
            position: 'relative',
            padding: 16,
            paddingTop: 0,
            paddingBottom: 0,
            zIndex: 5,
          }}
        >
          <Pressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              alignSelf: 'center',
              paddingTop: 6,
            }}
            onPress={() => {
              router.navigate('/');
            }}
          >
            {subdomain ? (
              <Image
                style={{
                  height: 72,
                  aspectRatio: 16 / 9,
                  opacity: imageLoaded ? 1 : 0,
                }}
                source={{
                  uri:
                    UnistylesRuntime.themeName === 'dark'
                      ? organization?.darkLogo
                      : organization?.lightLogo,
                }}
                contentFit="cover"
                onLoad={() => setImageLoaded(true)}
              />
            ) : (
              <Image
                style={{ width: 110, height: 50 }}
                source={
                  UnistylesRuntime.themeName === 'dark'
                    ? require('@/../public/images/logos/partner-logo-dark.png')
                    : require('@/../public/images/logos/partner-logo-light.png')
                }
                contentFit="contain"
              />
            )}
          </Pressable>

          <View
            ref={searchContainerRef}
            style={{
              gap: 16,
              padding: 8,
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                backgroundColor: theme.colors.background_2,
                borderRadius: 24,
                padding: 4,
                borderColor: theme.colors.card.default.borderColor,
                borderWidth: 1,
                alignItems: 'center',
              }}
            >
              <View style={{ width: 400 }}>
                {organization ? (
                  <OrganizationLocationInput style={{ width: '100%' }} />
                ) : (
                  <>
                    {!showGoogleAutoComplete ? (
                      <SearchBarButton
                        onPress={() => {
                          setShowStartTimeCalendar(false);
                          setShowEndTimeCalendar(false);
                          setShowGoogleAutoComplete(true);
                        }}
                        title={
                          deliveryDeliverToAddress?.description ||
                          address?.description ||
                          'Where'
                        }
                        caption={
                          deliveryDeliverToAddress?.description ||
                          address?.description
                            ? ''
                            : 'Address, airport, city...'
                        }
                        style={{
                          border: 'none',
                        }}
                      />
                    ) : null}

                    {showGoogleAutoComplete ? (
                      <GoogleAutocomplete
                        onPress={(d: Address) => {
                          setAddress({
                            description: d.description,
                            placeId: d.placeId,
                            latitude: d.latitude,
                            longitude: d.longitude,
                          });

                          setDeliveryModeEnabled(false);
                          setDeliveryDeliverToAddress(null);
                          setDeliveryReturnToAddress(null);

                          setShowGoogleAutoComplete(false);
                        }}
                        onBlur={() => {
                          // setShowGoogleAutoComplete(false);
                        }}
                        autoFocus
                      />
                    ) : null}
                  </>
                )}
              </View>

              <View style={{ position: 'relative' }}>
                <SearchBarButton
                  onPress={() => {
                    setShowGoogleAutoComplete(false);
                    setShowEndTimeCalendar(false);
                    setShowStartTimeCalendar(!showStartTimeCalendar);
                  }}
                  icon={<CalendarIcon />}
                  title="From"
                  caption={
                    (startDatetime &&
                      `${format(startDatetime, 'MMM d | h:mm a')}`) ||
                    undefined
                  }
                  style={{
                    width: 208,
                    ...(!showStartTimeCalendar && {
                      border: 'none',
                    }),
                  }}
                  active={showStartTimeCalendar}
                />

                <Calendar
                  isVisible={showStartTimeCalendar}
                  minDate={minDate}
                  defaultStartDate={defaultStartDate}
                  defaultEndDate={defaultEndDate}
                  onHide={() => {
                    setShowStartTimeCalendar(false);
                  }}
                  onDateSelected={(startDt) => {
                    const formattedStartDatetime = setMinutes(
                      setHours(startDt, getHours(startDatetime)),
                      getMinutes(startDatetime)
                    );

                    if (formattedStartDatetime < minDate) {
                      setStartDatetime(minDate);
                      return;
                    }

                    if (endDatetime && formattedStartDatetime >= endDatetime) {
                      setStartDatetime(formattedStartDatetime);

                      const newEndDatetime = addDays(formattedStartDatetime, 3);
                      setEndDatetime(newEndDatetime);

                      return;
                    }

                    setStartDatetime(formattedStartDatetime);
                  }}
                  onTimeSelected={(startDt) => {
                    if (endDatetime && startDt >= endDatetime) {
                      const newEndDatetime = addDays(startDt, 3);
                      setEndDatetime(newEndDatetime);
                    }

                    setStartDatetime(startDt);
                    setShowEndTimeCalendar(true);
                  }}
                  range={false}
                  style={{
                    zIndex: 1,
                    position: 'absolute',
                    top: 0,
                    right: 500,
                  }}
                  containerStyle={{
                    borderWidth: 1,
                    borderColor: theme.colors.card.default.borderColor,
                    shadowColor: theme.colors.card.default.borderColor,
                    shadowOffset: {
                      width: 0,
                      height: 5,
                    },
                    shadowOpacity: 0.5,
                    shadowRadius: 5,
                    elevation: 10,
                  }}
                  id="start-time-calendar"
                  hasBackdrop={false}
                  coverScreen={false}
                />
              </View>

              <View>
                <SearchBarButton
                  onPress={() => {
                    setShowGoogleAutoComplete(false);
                    setShowStartTimeCalendar(false);
                    setShowEndTimeCalendar(!showEndTimeCalendar);
                  }}
                  icon={<CalendarIcon />}
                  title="To"
                  caption={
                    (endDatetime &&
                      `${format(endDatetime, 'MMM d | h:mm a')}`) ||
                    undefined
                  }
                  style={{
                    width: 208,
                    ...(!showEndTimeCalendar && {
                      border: 'none',
                    }),
                  }}
                  active={showEndTimeCalendar}
                />

                <Calendar
                  isVisible={showEndTimeCalendar}
                  onHide={() => {
                    setShowEndTimeCalendar(false);
                  }}
                  minDate={addHours(defaultStartDate, 1)}
                  defaultStartDate={defaultEndDate}
                  onHide={() => {
                    setShowEndTimeCalendar(false);
                  }}
                  onDateSelected={(endDt) => {
                    setEndDatetime(
                      setMinutes(
                        setHours(endDt, getHours(endDatetime)),
                        getMinutes(endDatetime)
                      )
                    );
                  }}
                  onTimeSelected={(endDt) => {
                    setEndDatetime(endDt);
                    setShowEndTimeCalendar(false);
                  }}
                  range={false}
                  style={{
                    zIndex: 1,
                    position: 'absolute',
                    top: 0,
                    right: 600,
                  }}
                  containerStyle={{
                    borderWidth: 1,
                    borderColor: theme.colors.card.default.borderColor,
                    shadowColor: theme.colors.card.default.borderColor,
                    shadowOffset: {
                      width: 0,
                      height: 5,
                    },
                    shadowOpacity: 0.5,
                    shadowRadius: 5,
                    elevation: 10,
                  }}
                  id="end-time-calendar"
                  hasBackdrop={false}
                  coverScreen={false}
                />
              </View>
            </View>

            {searchTimesError && (
              <View
                style={{
                  paddingTop: 8,
                  position: 'absolute',
                  bottom: -16,
                  left: 8,
                  zIndex: -1,
                }}
              >
                <Text
                  style={{
                    color: theme.colors.error,
                    alignSelf: 'flex-start',
                    zIndex: 0,
                  }}
                >
                  {searchTimesError}
                </Text>
              </View>
            )}
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 16,
              alignSelf: 'center',
              paddingTop: 8,
            }}
          >
            {status === 'signIn' && customer?.partnerToken ? (
              <Button
                tx="navbar.switchToPartner"
                onPress={() => {
                  router.replace('/partner/dashboard');
                }}
                type="primarySmall"
                style={{ width: 180 }}
              />
            ) : (
              <Button
                tx="navbar.becomeAPartner"
                onPress={() => {
                  window.open('https://eonrides.com/partners', '_blank');
                }}
                type="secondaryExtraSmall"
                style={{ width: 130 }}
              />
            )}

            <Pressable
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
              hitSlop={8}
              onPress={() => {
                setShowDropdown(!showDropdown);
              }}
            >
              {status === 'signIn' &&
              customer &&
              (customer.firstname || customer.lastname) ? (
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <View
                    style={{
                      borderRadius: 100,
                      borderWidth: 1,
                      borderColor: theme.colors.card.hover.borderColor,
                      backgroundColor: theme.colors.card.hover.backgroundColor,
                      padding: 10,
                    }}
                  >
                    <Text
                      type="bodyBold"
                      style={{ color: theme.colors.icon.color }}
                    >
                      {`${customer?.firstname?.[0] || ''}${
                        customer?.lastname?.[0] || ''
                      }`}
                    </Text>
                  </View>
                </View>
              ) : (
                <Icon
                  type="dressed"
                  icon={<Person />}
                  fillColor={theme.colors.icon.color}
                  width={20}
                  height={20}
                  style={{ borderRadius: 20 }}
                />
              )}

              <Icon
                type="naked"
                icon={<ChevronRight />}
                fillColor={theme.colors.icon.color}
                width={12}
                height={12}
                iconStyle={{
                  transform: [{ rotate: '90deg' }],
                }}
                style={{ borderRadius: 20 }}
              />
            </Pressable>
          </View>

          <NavbarDropDown
            visible={showDropdown}
            onDismiss={() => {
              setShowDropdown(false);
            }}
            style={{ top: 100 }}
          />
        </View>

        <View style={{ padding: 16, flex: 1 }}>
          <View style={{ position: 'relative', zIndex: -1 }}>
            <View style={{ height: 50, flexDirection: 'row', gap: 8 }}>
              {filters && filters.length > 0
                ? filters
                    .filter((f) => f.name !== FilterName.SORT_BY)
                    .map((filter: Filter, idx) => {
                      const formattedName = titleize(filter.name);

                      if (filter.collapsed) {
                        return null;
                      }

                      return (
                        <ButtonFilter
                          key={filter.name}
                          name={
                            filter.selected.length > 0
                              ? `${formattedName} ${
                                  filter.name !== FilterName.PRICE
                                    ? `(${filter.selected.length})`
                                    : ''
                                }`
                              : formattedName
                          }
                          headerTitle={formattedName}
                          handlePress={(selectedOptions: number[]) => {
                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected: selectedOptions,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setTempFilters(updatedFilters);
                          }}
                          options={filter.options}
                          onOpen={() => {
                            setActiveFilterIdx(idx);
                          }}
                          onClose={() => {
                            setTempFilters([]);
                            setActiveFilterIdx(null);
                          }}
                          onClearAll={() => {
                            let selected: any[] = [];

                            if (filter.name === FilterName.PRICE) {
                              selected = [10, 1000];
                            }

                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setFilters(updatedFilters);

                            const updatedTempFilters = [
                              ...tempFilters.slice(0, idx),
                              {
                                ...filter,
                                selected,
                              },
                              ...tempFilters.slice(idx + 1),
                            ];

                            setTempFilters(updatedTempFilters);
                          }}
                          onSave={(selectedOptions: number[]) => {
                            const updatedFilters = [
                              ...filters.slice(0, idx),
                              {
                                ...filter,
                                selected: selectedOptions,
                              },
                              ...filters.slice(idx + 1),
                            ];

                            setFilters(updatedFilters);
                            setActiveFilterIdx(null);
                          }}
                          opened={activeFilterIdx === idx}
                          active={
                            activeFilterIdx === idx ||
                            (filter.name !== FilterName.PRICE &&
                              filter.selected.length > 0) ||
                            (filter.name === FilterName.PRICE &&
                              (filter.selected[0] !== 10 ||
                                filter.selected[1] !== 1000))
                          }
                          selected={filter.selected}
                          numResults={carsCount || 0}
                        />
                      );
                    })
                : null}

              <View>
                <Button
                  tx="search.resetFilters"
                  type="ghost"
                  onPress={() => {
                    const updatedFilters = filters.map((filter) => {
                      let selected: any = [];

                      if (filter.name === FilterName.PRICE) {
                        selected = [10, 1000];
                      } else if (filter.name === FilterName.SORT_BY) {
                        selected = 0;
                      }

                      return {
                        ...filter,
                        selected,
                      };
                    });

                    setFilters(updatedFilters);
                  }}
                />
              </View>
            </View>
          </View>

          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              position: 'relative',
              zIndex: -1,
            }}
          >
            <View
              style={{
                flex: 1,
                width: viewMode === 'list' ? '100%' : '50%',
                paddingRight: viewMode === 'list' ? 0 : 32,
                gap: 16,
              }}
            >
              <View style={{ gap: 8 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                  }}
                >
                  {filters?.find((f) => f.name === FilterName.SORT_BY)?.options
                    .length > 0 ? (
                    <ButtonFilter
                      style={{
                        width: 200,
                      }}
                      name={
                        filters?.find((f) => f.name === FilterName.SORT_BY)
                          ?.options[
                          filters?.find((f) => f.name === FilterName.SORT_BY)
                            ?.selected
                        ].name
                      }
                      headerTitle={'Sort By'}
                      handlePress={(selectedOptions: number[]) => {
                        const sortByIdx = filters.findIndex(
                          (f) => f.name === FilterName.SORT_BY
                        );

                        const filter = filters[sortByIdx];

                        const updatedFilters = [
                          ...filters.slice(0, sortByIdx),
                          {
                            ...filter,
                            selected: selectedOptions,
                          },
                          ...filters.slice(sortByIdx + 1),
                        ];

                        setTempFilters(updatedFilters);
                      }}
                      options={
                        filters?.find((f) => f.name === FilterName.SORT_BY)
                          ?.options || []
                      }
                      onOpen={() => {
                        const sortByIdx = filters.findIndex(
                          (f) => f.name === FilterName.SORT_BY
                        );

                        setActiveFilterIdx(sortByIdx);
                      }}
                      onClose={() => {
                        setTempFilters([]);
                        setActiveFilterIdx(null);
                      }}
                      onClearAll={() => {
                        const sortByIdx = filters.findIndex(
                          (f) => f.name === FilterName.SORT_BY
                        );

                        const filter = filters[sortByIdx];

                        let selected: any[] = [];

                        const updatedFilters = [
                          ...filters.slice(0, sortByIdx),
                          {
                            ...filter,
                            selected,
                          },
                          ...filters.slice(sortByIdx + 1),
                        ];

                        setFilters(updatedFilters);

                        const updatedTempFilters = [
                          ...tempFilters.slice(0, sortByIdx),
                          {
                            ...filter,
                            selected,
                          },
                          ...tempFilters.slice(sortByIdx + 1),
                        ];

                        setTempFilters(updatedTempFilters);
                      }}
                      onSave={(selectedOptions: number[]) => {
                        const sortByIdx = filters.findIndex(
                          (f) => f.name === FilterName.SORT_BY
                        );

                        const filter = filters[sortByIdx];

                        const updatedFilters = [
                          ...filters.slice(0, sortByIdx),
                          {
                            ...filter,
                            selected: selectedOptions,
                          },
                          ...filters.slice(sortByIdx + 1),
                        ];

                        setFilters(updatedFilters);
                      }}
                      // active={
                      //   activeFilterIdx ===
                      //   filters.findIndex((f) => f.name === FilterName.SORT_BY)
                      // }
                      selected={
                        filters?.find((f) => f.name === FilterName.SORT_BY)
                          ?.selected
                      }
                      numResults={carsCount || 0}
                    />
                  ) : (
                    <View />
                  )}

                  <DeliveryDropdown
                    style={{ width: 200 }}
                    active={deliveryModeEnabled}
                    initialDeliveryAddress={deliveryDeliverToAddress}
                    initialReturnAddress={deliveryReturnAddress}
                  />
                </View>

                {cars && cars.length > 0 ? (
                  <View style={{ paddingLeft: 16 }}>
                    <Text
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {`${cars?.length} of ${totalCarsCount}`}
                    </Text>
                  </View>
                ) : null}
              </View>

              <View style={{ flex: 1 }}>
                {isFetching && !isFetchingNextPage ? (
                  <View
                    style={{
                      width: '100%',
                      height: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Loader style={{ height: 150, width: 150 }} />
                  </View>
                ) : null}

                {!isFetching && (!cars || cars?.length === 0) ? (
                  <View
                    style={{
                      flexGrow: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 16,
                      gap: 16,
                    }}
                  >
                    <Image
                      style={{ width: 200, height: 200 }}
                      source={
                        UnistylesRuntime.themeName === 'dark'
                          ? require('../../../../public/images/empty-road-dark.png')
                          : require('../../../../public/images/empty-road-light.png')
                      }
                      contentFit="cover"
                    />

                    <View
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Text type="title1">No Cars Available</Text>

                      <Text
                        type="bodyRegular"
                        style={{
                          color: theme.colors.text.secondary,
                          textAlign: 'center',
                        }}
                      >
                        Try Changing Your Filters, Dates, or Zooming In on the
                        Map.
                      </Text>
                    </View>
                  </View>
                ) : null}

                <FlashList
                  key={viewMode}
                  data={cars}
                  numColumns={viewMode === 'list' ? 4 : 1}
                  renderItem={({ item }) => (
                    <View
                      style={viewMode === 'list' ? styles.gridItem : undefined}
                    >
                      <CarCard
                        car={item}
                        stacked={viewMode === 'list'}
                        onPress={() => {
                          setCar(item);

                          if (Platform.OS === 'web') {
                            const queryParams = new URLSearchParams();

                            if (startDatetime) {
                              queryParams.append(
                                'start_datetime',
                                format(startDatetime, 'yyyy-MM-dd HH:mm:ss')
                              );
                            }

                            if (endDatetime) {
                              queryParams.append(
                                'end_datetime',
                                format(endDatetime, 'yyyy-MM-dd HH:mm:ss')
                              );
                            }

                            if (deliveryModeEnabled) {
                              queryParams.append('mode', 'delivery');
                            } else {
                              queryParams.append('mode', 'pickup');
                            }

                            if (deliveryDeliverToAddress?.description) {
                              queryParams.append(
                                'delivery_address',
                                deliveryDeliverToAddress.description
                              );
                            }

                            if (deliveryDeliverToAddress?.latitude) {
                              queryParams.append(
                                'delivery_address_lat',
                                deliveryDeliverToAddress.latitude.toString()
                              );
                            }

                            if (deliveryDeliverToAddress?.longitude) {
                              queryParams.append(
                                'delivery_address_lng',
                                deliveryDeliverToAddress.longitude.toString()
                              );
                            }

                            if (deliveryReturnAddress?.description) {
                              queryParams.append(
                                'return_address',
                                deliveryReturnAddress.description
                              );
                            }

                            if (deliveryReturnAddress?.latitude) {
                              queryParams.append(
                                'return_address_lat',
                                deliveryReturnAddress.latitude.toString()
                              );
                            }

                            if (deliveryReturnAddress?.longitude) {
                              queryParams.append(
                                'return_address_lng',
                                deliveryReturnAddress.longitude.toString()
                              );
                            }

                            if (local.code) {
                              queryParams.append('code', local.code);
                            }

                            if (pid) {
                              queryParams.append('pid', pid);
                            }

                            if (c) {
                              queryParams.append('c', c);
                            }

                            if (local.promo) {
                              queryParams.append('promo', local.promo);
                            }

                            const url = `/car/${item.id}?${queryParams}`;

                            window.open(url, '_blank');
                          } else {
                            router.push({
                              pathname: '/car/[id]',
                              params: {
                                id: item.id,
                                start_datetime: format(
                                  startDatetime,
                                  'yyyy-MM-dd HH:mm:ss'
                                ),
                                end_datetime: format(
                                  endDatetime,
                                  'yyyy-MM-dd HH:mm:ss'
                                ),
                                delivery_address:
                                  deliveryDeliverToAddress?.description,
                                delivery_address_lat:
                                  deliveryDeliverToAddress?.latitude,
                                delivery_address_lng:
                                  deliveryDeliverToAddress?.longitude,
                                return_address:
                                  deliveryReturnAddress?.description,
                                return_address_lat:
                                  deliveryReturnAddress?.latitude,
                                return_address_lng:
                                  deliveryReturnAddress?.longitude,
                                mode: deliveryModeEnabled
                                  ? 'delivery'
                                  : 'pickup',
                                code: local.code || undefined,
                                pid: pid || undefined,
                                c: c || undefined,
                                promo: local.promo || undefined,
                              },
                            });
                          }
                        }}
                      />
                    </View>
                  )}
                  ItemSeparatorComponent={
                    viewMode === 'list'
                      ? undefined
                      : () => <View style={styles.carItemSeparator} />
                  }
                  onEndReached={() => {
                    if (hasNextPage && !isFetchingNextPage) {
                      console.log('end reached');
                      fetchNextPage();
                    }
                  }}
                  estimatedItemSize={viewMode === 'list' ? 380 : 220}
                  contentContainerStyle={{
                    paddingBottom: 100,
                  }}
                  showsVerticalScrollIndicator={false}
                  ListFooterComponent={() => {
                    if (!isFetchingNextPage) return null;

                    return <Loader style={{ height: 140 }} />;
                  }}
                />
              </View>
            </View>

            {viewMode !== 'list' ? (
              <View
                style={{
                  width: '50%',
                  height: '100%',
                  borderRadius: 16,
                  position: 'relative',
                  zIndex: 1,
                  overflow: 'hidden',
                }}
              >
                <MapView
                  ref={mapRef}
                  polylines={[]}
                  markers={markers}
                  onPress={() => {
                    setSelectedLocation(undefined);
                    setLocationId(undefined);
                  }}
                  onDrag={() => {
                    setEnableMapRefresh(true);
                  }}
                  onLoad={async () => {
                    const bounds = await mapRef.current?.getBounds();

                    setBoundaries({
                      topRight: {
                        lat: bounds.northEast.latitude,
                        lng: bounds.northEast.longitude,
                      },
                      bottomLeft: {
                        lat: bounds.southWest.latitude,
                        lng: bounds.southWest.longitude,
                      },
                    });
                  }}
                  defaultCenter={defaultCenter}
                  containerStyle={{ borderRadius: 16 }}
                  fitBounds={deliveryModeEnabled}
                />

                {enableMapRefresh ? (
                  <Button
                    tx="search.refresh"
                    type="secondaryExtraSmall"
                    leftIcon={
                      <Icon
                        type="naked"
                        icon={<ArrowClockwise />}
                        width={12}
                        height={12}
                        fillColor={theme.colors.button.secondary.default.text}
                      />
                    }
                    onPress={async () => {
                      const bounds = await mapRef.current?.getBounds();

                      setBoundaries({
                        topRight: {
                          lat: bounds.northEast.latitude,
                          lng: bounds.northEast.longitude,
                        },
                        bottomLeft: {
                          lat: bounds.southWest.latitude,
                          lng: bounds.southWest.longitude,
                        },
                      });

                      setLocationId(undefined);
                      setEnableMapRefresh(false);
                    }}
                    style={{
                      width: 100,
                      position: 'absolute',
                      top: 50,
                      left: '50%',
                      transform: [{ translateX: -50 }],
                    }}
                  />
                ) : null}
              </View>
            ) : null}
          </View>
        </View>

        <EonOfferModal
          visible={showEonOfferModal}
          onClose={() => {
            setShowEonOfferModal(false);
          }}
          onClick={async () => {
            setShowEonOfferModal(false);
          }}
          title={discount?.meta.title}
          body={discount?.meta.body}
          code={discount?.code}
          expiresAt={discount?.endDate}
        />
      </View>
    );
  }

  return (
    <View style={[styles.container]}>
      {!boundaries &&
      !address &&
      !(deliveryModeEnabled && deliveryDeliverToAddress) ? (
        // Default landing state: render the shared Home experience full-screen
        // so native matches mobile web (hero + inline Pickup/Delivery card).
        // Kept ahead of the viewMode branches so toggling Delivery never drops
        // into the map view, and Home's ScrollView gets a bounded parent. We
        // only leave the landing once a real search exists — a pickup address,
        // a delivery address, or map boundaries.
        <View style={{ flex: 1 }}>
          <Home />
        </View>
      ) : viewMode === 'list' ? (
        <>
          <View
            style={{
              gap: 8,
              backgroundColor: theme.colors.background_2,
              paddingLeft: 16,
              paddingRight: 16,
              paddingBottom: 8,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
              paddingTop: UnistylesRuntime.insets.top + 15,
            }}
          >
            {renderSearchAndFilters()}
          </View>

          {isFetching && !isFetchingNextPage ? (
            <View
              style={{
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
              }}
            >
              <Loader style={{ height: 150, width: 150 }} />
            </View>
          ) : null}

          {!boundaries && !address && !deliveryModeEnabled
            ? renderNativeHome()
            : null}

          {cars?.length > 0 ? (
            <View
              style={{
                gap: 16,
                paddingLeft: 16,
                paddingRight: 16,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View>
                <Text type="bodyRegular">{`${cars?.length} of ${totalCarsCount}`}</Text>
              </View>

              <View>
                <ButtonFilter
                  name={
                    filters?.find((f) => f.name === FilterName.SORT_BY)
                      ?.options[
                      filters?.find((f) => f.name === FilterName.SORT_BY)
                        ?.selected
                    ].name
                  }
                  headerTitle={'Sort By'}
                  handlePress={(selectedOptions: number[]) => {
                    const sortByIdx = filters.findIndex(
                      (f) => f.name === FilterName.SORT_BY
                    );

                    const filter = filters[sortByIdx];

                    const updatedFilters = [
                      ...filters.slice(0, sortByIdx),
                      {
                        ...filter,
                        selected: selectedOptions,
                      },
                      ...filters.slice(sortByIdx + 1),
                    ];

                    setTempFilters(updatedFilters);
                  }}
                  options={
                    filters?.find((f) => f.name === FilterName.SORT_BY)
                      ?.options || []
                  }
                  onOpen={() => {
                    const sortByIdx = filters.findIndex(
                      (f) => f.name === FilterName.SORT_BY
                    );

                    setActiveFilterIdx(sortByIdx);
                  }}
                  onClose={() => {
                    setTempFilters([]);
                    setActiveFilterIdx(null);
                  }}
                  onClearAll={() => {
                    const sortByIdx = filters.findIndex(
                      (f) => f.name === FilterName.SORT_BY
                    );

                    const filter = filters[sortByIdx];

                    let selected: any[] = [];

                    const updatedFilters = [
                      ...filters.slice(0, sortByIdx),
                      {
                        ...filter,
                        selected,
                      },
                      ...filters.slice(sortByIdx + 1),
                    ];

                    setFilters(updatedFilters);

                    const updatedTempFilters = [
                      ...tempFilters.slice(0, sortByIdx),
                      {
                        ...filter,
                        selected,
                      },
                      ...tempFilters.slice(sortByIdx + 1),
                    ];

                    setTempFilters(updatedTempFilters);
                  }}
                  onSave={(selectedOptions: number[]) => {
                    const sortByIdx = filters.findIndex(
                      (f) => f.name === FilterName.SORT_BY
                    );

                    const filter = filters[sortByIdx];

                    const updatedFilters = [
                      ...filters.slice(0, sortByIdx),
                      {
                        ...filter,
                        selected: selectedOptions,
                      },
                      ...filters.slice(sortByIdx + 1),
                    ];

                    setFilters(updatedFilters);
                  }}
                  // active={
                  //   activeFilterIdx ===
                  //   filters.findIndex((f) => f.name === FilterName.SORT_BY)
                  // }
                  selected={
                    filters?.find((f) => f.name === FilterName.SORT_BY)
                      ?.selected
                  }
                  numResults={carsCount || 0}
                />
              </View>
            </View>
          ) : null}

          {(boundaries || address) && cars?.length === 0 ? (
            <View
              style={{
                flexGrow: 1,
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                gap: 16,
              }}
            >
              <Image
                style={{ width: 200, height: 200 }}
                source={
                  UnistylesRuntime.themeName === 'dark'
                    ? require('../../../../public/images/empty-road-dark.png')
                    : require('../../../../public/images/empty-road-light.png')
                }
                contentFit="cover"
              />

              <View
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text type="title1">No Cars Available</Text>

                <Text
                  type="bodyRegular"
                  style={{
                    color: theme.colors.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  Try Changing Your Filters, Dates, or Zooming In on the Map.
                </Text>
              </View>
            </View>
          ) : null}

          <View style={{ flex: 1 }}>
            <FlashList
              data={cars}
              renderItem={({ item }) => (
                <CarCard
                  car={item}
                  onPress={() => {
                    setCar(item);
                    router.push({
                      pathname: '/car/[id]',
                      params: {
                        id: item.id,
                        start_datetime: format(
                          startDatetime,
                          'yyyy-MM-dd HH:mm:ss'
                        ),
                        end_datetime: format(
                          endDatetime,
                          'yyyy-MM-dd HH:mm:ss'
                        ),
                        delivery_address: deliveryDeliverToAddress?.description,
                        delivery_address_lat:
                          deliveryDeliverToAddress?.latitude,
                        delivery_address_lng:
                          deliveryDeliverToAddress?.longitude,
                        return_address: deliveryReturnAddress?.description,
                        return_address_lat: deliveryReturnAddress?.latitude,
                        return_address_lng: deliveryReturnAddress?.longitude,
                        mode: deliveryModeEnabled ? 'delivery' : 'pickup',
                        code: local.code || undefined,
                        pid: pid || undefined,
                        c: c || undefined,
                      },
                    });
                  }}
                />
              )}
              ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
              onEndReached={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  console.log('end reached');
                  fetchNextPage();
                }
              }}
              contentContainerStyle={{
                paddingLeft: 16,
                paddingRight: 16,
                paddingBottom: 125,
              }}
              estimatedItemSize={20}
              showsVerticalScrollIndicator={false}
              ListFooterComponent={() => {
                if (!isFetchingNextPage) return null;

                return <Loader style={{ height: 140 }} />;
              }}
            />

            {isFetched && (boundaries || address) ? (
              <View
                style={{
                  position: 'absolute',
                  bottom: modeButtonBottomOffset,
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Button
                  tx="search.mapView"
                  type="primaryExtraSmall"
                  leftIcon={
                    <Icon
                      type="naked"
                      icon={<MapIcon />}
                      fillColor={theme.colors.button.primary.default.text}
                      width={12}
                      height={12}
                    />
                  }
                  onPress={() => {
                    setViewMode('map');
                  }}
                  style={{
                    width: 115,
                  }}
                />
              </View>
            ) : null}
          </View>
        </>
      ) : (
        <View style={{ flexGrow: 1 }}>
          <MapView
            ref={mapRef}
            polylines={[]}
            markers={markers}
            onPress={() => {
              setSelectedLocation(undefined);
            }}
            onDrag={() => {
              setEnableMapRefresh(true);
            }}
            onLoad={async () => {
              const bounds = await mapRef.current?.getBounds();
              setBoundaries({
                topRight: {
                  lat: bounds.northEast.latitude,
                  lng: bounds.northEast.longitude,
                },
                bottomLeft: {
                  lat: bounds.southWest.latitude,
                  lng: bounds.southWest.longitude,
                },
              });
            }}
            defaultCenter={defaultCenter}
            fitBounds={deliveryModeEnabled}
            style={{ borderRadius: 0 }}
          />

          {!boundaries && !address && !deliveryModeEnabled ? (
            <View
              style={{
                gap: 8,
                backgroundColor: theme.colors.background_2,
                paddingLeft: 16,
                paddingRight: 16,
                paddingBottom: 8,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
                paddingTop: UnistylesRuntime.insets.top + 15,
              }}
            >
              {renderSearchAndFilters()}
            </View>
          ) : (
            <View
              style={{
                position: 'absolute',
                width: '100%',
                paddingTop: UnistylesRuntime.insets.top + 15,
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  width: '90%',
                  gap: 8,
                }}
              >
                {renderSearchAndFilters()}
              </View>
            </View>
          )}

          {isFetching ? (
            <View
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                inset: 0,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loader style={{ width: 150, height: 150 }} />
            </View>
          ) : null}

          {!boundaries && !address && !deliveryModeEnabled
            ? renderNativeHome()
            : null}

          <View
            style={{
              position: 'absolute',
              bottom: 18,
              width: '100%',
            }}
          >
            {locationCars.length > 1 ? (
              <LinearGradient
                pointerEvents="none"
                colors={[
                  theme.colors.background_1.replace(/[\d.]+\)$/, '0)'),
                  theme.colors.background_1,
                  theme.colors.background_1,
                ]}
                locations={[0, 0.35, 1]}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: -64,
                  bottom: -18,
                }}
              />
            ) : null}

            <View
              style={{
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Button
                tx="search.listView"
                type="primaryExtraSmall"
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<List />}
                    fillColor={theme.colors.button.primary.default.text}
                    width={12}
                    height={12}
                  />
                }
                onPress={() => {
                  setViewMode('list');
                }}
                style={{
                  width: 115,
                }}
              />
            </View>

            {locationCars.length > 0 && selectedLocation ? (
              <MapCarCarousel
                cars={locationCars}
                selectionKey={String(selectedLocation.id)}
                onCarPress={(item) => {
                  setCar(item);

                  if (Platform.OS === 'web' && isDesktop()) {
                    const queryParams = new URLSearchParams();

                    if (startDatetime) {
                      queryParams.append(
                        'start_datetime',
                        format(startDatetime, 'yyyy-MM-dd HH:mm:ss')
                      );
                    }

                    if (endDatetime) {
                      queryParams.append(
                        'end_datetime',
                        format(endDatetime, 'yyyy-MM-dd HH:mm:ss')
                      );
                    }

                    if (deliveryModeEnabled) {
                      queryParams.append('mode', 'delivery');
                    } else {
                      queryParams.append('mode', 'pickup');
                    }

                    if (deliveryDeliverToAddress?.description) {
                      queryParams.append(
                        'delivery_address',
                        deliveryDeliverToAddress.description
                      );
                    }

                    if (deliveryDeliverToAddress?.latitude) {
                      queryParams.append(
                        'delivery_address_lat',
                        deliveryDeliverToAddress.latitude.toString()
                      );
                    }

                    if (deliveryDeliverToAddress?.longitude) {
                      queryParams.append(
                        'delivery_address_lng',
                        deliveryDeliverToAddress.longitude.toString()
                      );
                    }

                    if (deliveryReturnAddress?.description) {
                      queryParams.append(
                        'return_address',
                        deliveryReturnAddress.description
                      );
                    }

                    if (deliveryReturnAddress?.latitude) {
                      queryParams.append(
                        'return_address_lat',
                        deliveryReturnAddress.latitude.toString()
                      );
                    }

                    if (deliveryReturnAddress?.longitude) {
                      queryParams.append(
                        'return_address_lng',
                        deliveryReturnAddress.longitude.toString()
                      );
                    }

                    if (local.code) {
                      queryParams.append('code', local.code);
                    }

                    if (pid) {
                      queryParams.append('pid', pid);
                    }

                    if (c) {
                      queryParams.append('c', c);
                    }

                    if (local.promo) {
                      queryParams.append('promo', local.promo);
                    }

                    const url = `/car/${item.id}?${queryParams}`;

                    window.open(url, '_blank');
                  } else {
                    router.push({
                      pathname: '/car/[id]',
                      params: {
                        id: item.id,
                        start_datetime: format(
                          defaultStartDate,
                          'yyyy-MM-dd HH:mm:ss'
                        ),
                        end_datetime: format(
                          defaultEndDate,
                          'yyyy-MM-dd HH:mm:ss'
                        ),
                        delivery_address: deliveryDeliverToAddress?.description,
                        delivery_address_lat:
                          deliveryDeliverToAddress?.latitude,
                        delivery_address_lng:
                          deliveryDeliverToAddress?.longitude,
                        return_address: deliveryReturnAddress?.description,
                        return_address_lat: deliveryReturnAddress?.latitude,
                        return_address_lng: deliveryReturnAddress?.longitude,
                        mode: deliveryModeEnabled ? 'delivery' : 'pickup',
                        code: local.code || undefined,
                        pid: pid || undefined,
                        c: c || undefined,
                        promo: local.promo || undefined,
                      },
                    });
                  }
                }}
              />
            ) : null}
          </View>
        </View>
      )}

      <EonOfferModal
        visible={showEonOfferModal}
        onClose={() => {
          setShowEonOfferModal(false);
        }}
        onClick={async () => {
          setShowEonOfferModal(false);
        }}
        title={discount?.meta.title}
        body={discount?.meta.body}
        code={discount?.code}
        expiresAt={discount?.endDate}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    maxHeight: PAGE_HEIGHT,
  },
  carItemSeparator: {
    height: {
      xs: 16,
      md: 8,
    },
  },
  gridItem: {
    flex: 1,
    padding: 8,
    alignSelf: 'flex-start',
  },
}));
